import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CalendarDays, Clock3, MapPin, CheckCircle2,
  ShieldCheck, ListChecks, Gift, ChevronLeft, ChevronRight,
} from "lucide-react";
import config, { fetchRemoteConfig, getMembershipPricing } from "../../config";
import { getPlanById, getTimeSlotsForPlan, formatInr, addDays } from "./membershipPlansData";
import { SILVER_PACKAGE_DURATIONS, SILVER_PACKAGE_SERVICES_PER_MONTH } from "../../config/pricing";

import kitchenImg   from "../images/stage_01/kitchen_utensil_cleaning.png";
import bathroomImg  from "../images/stage_01/bathroom_cleaning.png";
import balconyImg   from "../images/stage_01/balcony_cleaning.png";
import windowFanImg from "../images/stage_01/window,fan& switch board cleaning.png";
import moppingImg   from "../images/stage_01/mopping_cleaning.png";
import utensilsImg  from "../images/stage_01/utils_cleaning.png";
import wardrobeImg  from "../images/stage_01/wordrobe_cleaning.png";
import furnitureImg from "../images/stage_01/furniture & display cabients cleaning.png";
import cookingPrepImg from "../images/stage_01/cooking preparation.png";
import dustingWipingImg from "../images/stage_01/sweeping,dusting,mopping,wiping.png";
import glassImg      from "../images/stage_01/glass_cleaning.png";
import cabinetImg    from "../images/stage_01/cabinet_cleaning.png";
import packingImg    from "../images/stage_01/packing_unpacking.png";
import fridgeImg     from "../images/stage_01/refrigetor_cleaning.png";
import microwaveImg  from "../images/stage_01/refrigarator,chimney,microwave cleaning.png";

const BASE_URL = config.API_BASE_URL || "http://localhost:8000";
const PLAN_ID  = "silver-package";

// ── Auth / address helpers (same pattern as the other checkout pages) ──────
const getLoggedInUser = () => {
  try { return JSON.parse(localStorage.getItem("currentUser") || "null"); }
  catch { return null; }
};
const getAuthToken = () =>
  localStorage.getItem("authToken")    ||
  localStorage.getItem("accessToken")  ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") || "";
const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

// Bronze requires booking at least 2 days ahead (not just tomorrow).
const earliestStartDateStr = () => {
  const d = new Date();
  d.setDate(d.getDate() + 2);
  return d.toISOString().slice(0, 10);
};

// Bronze Package's fixed 17-item scope, each shown as an image card — "4
// at a time" carousel. Names match BRONZE_PACKAGE_INCLUDED_SERVICES in
// membershipPlansData.js exactly.
const INCLUDED_SERVICE_CARDS = [
  { name: "Kitchen Cleaning",       image: kitchenImg,       desc: "Full kitchen deep clean" },
  { name: "Utensil Cleaning",       image: utensilsImg,      desc: "Kitchen utensils washed & dried" },
  { name: "Food Preparation",       image: cookingPrepImg,   desc: "Meal prep assistance" },
  { name: "Sweeping & Mopping",     image: moppingImg,       desc: "Full floor sweep, mop & sanitize" },
  { name: "Dusting & Wiping",       image: dustingWipingImg, desc: "Surfaces, shelves & fixtures" },
  { name: "Fan & Ceiling",          image: windowFanImg,     desc: "Blade dusting & wipe-down" },
  { name: "Bathroom Cleaning",      image: bathroomImg,      desc: "Tiles, fittings & floor" },
  { name: "Furniture Cleaning",     image: furnitureImg,     desc: "Dusting & polish" },
  { name: "Wardrobe Arrangement",   image: wardrobeImg,      desc: "Outer wipe & tidy-up" },
  { name: "Window & Glass / Wall & Door", image: glassImg,   desc: "Glass, frame & door wipe-down" },
  { name: "Balcony & Entrance Cleaning",  image: balconyImg, desc: "Floor, railing & corners" },
  { name: "Kitchen Cabinet Cleaning",     image: cabinetImg, desc: "Outer wipe & tidy-up" },
  { name: "Packing & Unpacking",    image: packingImg,       desc: "Careful handling & arrangement" },
  { name: "Refrigerator Cleaning",  image: fridgeImg,        desc: "Interior & exterior wipe-down" },
  { name: "Microwave / Oven / Grill Cleaning", image: microwaveImg, desc: "Degreased & sanitized" },
  { name: "Display Cabinet / Showcase Cleaning", image: furnitureImg, desc: "Dusting & polish" },
];
const INCLUDED_VISIBLE = 4; // cards visible at once — "move one by one" via the arrows

// ─────────────────────────────────────────────────────────────────────────────
// STEP 2 — PAYMENT
// ─────────────────────────────────────────────────────────────────────────────
const PaymentStep = ({ plan, paymentOrder, bookingDetails, onBack, onActivated, onSlotConflict }) => {
  const [paying, setPaying] = useState(false);
  const [error, setError]   = useState("");

  const handlePay = async () => {
    if (paying) return;
    setPaying(true);
    setError("");
    try {
      const verifyRes = await fetch(`${BASE_URL}/api/payment/verify/`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          razorpay_order_id: paymentOrder.order_id,
          razorpay_payment_id: paymentOrder.is_test ? `pay_TEST_${Date.now()}` : undefined,
        }),
      });
      const verifyData = await verifyRes.json();
      if (!verifyRes.ok || !verifyData?.success) {
        throw new Error(verifyData?.message || "Payment verification failed.");
      }

      const purchaseRes = await fetch(`${BASE_URL}/api/user/membership/purchase/`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          ...bookingDetails,
          razorpay_payment_id:     verifyData.payment_id,
          razorpay_order_id:       paymentOrder.order_id,
          razorpay_transaction_id: verifyData.transaction_id,
          payment_status:          "paid",
        }),
      });
      const purchaseData = await purchaseRes.json();
      if (!purchaseRes.ok || !purchaseData?.success) {
        if (purchaseRes.status === 409) {
          onSlotConflict(purchaseData?.message || "That visit slot just filled up. Please choose a different date or time.");
          return;
        }
        throw new Error(purchaseData?.message || "Could not activate membership.");
      }

      onActivated(purchaseData);
    } catch (e) {
      setError(e.message || "Payment failed. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={{ ...S.container, maxWidth: "620px" }}>
        <button type="button" style={S.backLink} onClick={onBack}><ArrowLeft size={14} /> Back to Details</button>

        {paymentOrder.is_test && (
          <div style={S.testBanner}>🧪 <strong>Test Mode</strong> — no real payment will be processed.</div>
        )}

        <div style={S.card}>
          <div style={S.cardLabel}>Complete Your Payment</div>
          <p style={{ ...S.includedNote, marginBottom: "18px" }}>
            Razorpay Order ID: <strong>{paymentOrder.order_id}</strong>
          </p>

          <div style={S.summaryRow}><span>Plan</span><span style={S.summaryVal}>{plan.name}</span></div>
          <div style={S.summaryRow}><span>Duration</span><span style={S.summaryVal}>{bookingDetails.duration_months} Months</span></div>
          {bookingDetails.free_gift && (
            <div style={S.summaryRow}><span>Free Gift</span><span style={S.summaryVal}>{bookingDetails.free_gift}</span></div>
          )}
          <div style={S.summaryRow}><span>Visit Slot</span><span style={S.summaryVal}>{bookingDetails.preferred_slot}</span></div>
          <div style={S.summaryRow}><span>Start Date</span><span style={S.summaryVal}>{bookingDetails.start_date}</span></div>
          <div style={S.summaryDivider} />
          <div style={{ ...S.summaryRow, ...S.summaryTotal }}><span>Total Payable</span><span>{formatInr(bookingDetails.price)}</span></div>

          {error && <div style={S.errorBox}>{error}</div>}

          <button
            style={{ ...S.primaryBtn, opacity: paying ? 0.7 : 1 }}
            disabled={paying}
            onClick={handlePay}
          >
            {paying ? "Processing…" : `Pay ${formatInr(bookingDetails.price)}`}
          </button>
          <div style={S.secureNote}><ShieldCheck size={13} /> Secured by Razorpay{paymentOrder.is_test ? " (test mode)" : ""}</div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN — step router: "details" -> "payment" -> success
// ─────────────────────────────────────────────────────────────────────────────
const SilverPackageCheckout = () => {
  const navigate = useNavigate();

  const plan      = getPlanById(PLAN_ID);
  const timeSlots = getTimeSlotsForPlan(PLAN_ID);

  const loggedInUser = getLoggedInUser();
  const userMobile   = loggedInUser?.mobile || loggedInUser?.phone || "";
  const addressKey   = loggedInUser?.id || loggedInUser?.user_id || userMobile;

  const [step, setStep]                     = useState("details"); // "details" | "payment"
  const [paymentOrder, setPaymentOrder]     = useState(null);
  const [bookingDetails, setBookingDetails] = useState(null);

  const [durationMonths, setDurationMonths] = useState(SILVER_PACKAGE_DURATIONS[0].months);
  const [startDate, setStartDate]           = useState(earliestStartDateStr());
  const [slotId, setSlotId]                 = useState("");
  const [slotAvailability, setSlotAvailability] = useState([]);
  const [loadingSlots, setLoadingSlots]     = useState(false);
  const [carouselIndex, setCarouselIndex]   = useState(0);
  const [addresses, setAddresses]           = useState([]);
  const [addrLoading, setAddrLoading]       = useState(false);
  const [selectedAddrId, setSelectedAddrId] = useState("");
  const [initiating, setInitiating]         = useState(false);
  const [error, setError]                   = useState("");
  const [success, setSuccess]               = useState(null);

  // Dashboard-editable price/GST overrides (Seat Availability tab ->
  // "Membership Plan Pricing & GST") arrive async — force one re-render once
  // fetched so the plain (unmemoized) getMembershipPricing() reads below
  // pick up the live values instead of whatever was cached at module load.
  const [, forceConfigRerender] = useState(0);
  useEffect(() => {
    fetchRemoteConfig().then(() => forceConfigRerender((n) => n + 1));
  }, []);

  // Redirect to login if not signed in, preserving return path.
  useEffect(() => {
    if (!loggedInUser) {
      navigate("/user/user-login", {
        state: { redirectTo: `/subscription-plans/checkout/${PLAN_ID}` },
        replace: true,
      });
    }
  }, [loggedInUser, navigate]); // eslint-disable-line

  // Fetch saved addresses once logged in.
  useEffect(() => {
    if (!addressKey) return;
    setAddrLoading(true);
    fetch(`${BASE_URL}/api/user/user-addresses/${encodeURIComponent(addressKey)}/`, {
      credentials: "include", headers: authHeaders(),
    })
      .then((r) => r.json())
      .then((data) => {
        const addrs = Array.isArray(data?.data) ? data.data : [];
        setAddresses(addrs);
        if (addrs.length) setSelectedAddrId(addrs[0].id);
      })
      .catch(() => {})
      .finally(() => setAddrLoading(false));
  }, [addressKey]);

  // Fetch seat availability for the recurring visit slot whenever the start
  // date changes — same atomic "schedule concept" seat model used across
  // every membership plan, scoped to (plan_id, start_date).
  useEffect(() => {
    if (!startDate) return;
    let cancelled = false;
    setLoadingSlots(true);
    fetch(`${BASE_URL}/api/membership/slot-availability/`, {
      method: "POST", headers: authHeaders(),
      body: JSON.stringify({ plan_id: PLAN_ID, start_date: startDate }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        const slots = Array.isArray(data?.data?.slots) ? data.data.slots : [];
        setSlotAvailability(slots);
        setSlotId((prev) => {
          const stillOpen = slots.find((s) => s.slot_id === prev)?.availability;
          return stillOpen ? prev : "";
        });
      })
      .catch(() => { if (!cancelled) setSlotAvailability([]); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [startDate, step]);

  if (!plan) {
    return (
      <div style={S.page}>
        <div style={{ ...S.container, textAlign: "center", paddingTop: "80px" }}>
          <h2>Plan not found</h2>
          <Link to="/subscription-plans" style={S.backLink}><ArrowLeft size={14} /> Back to Plans</Link>
        </div>
      </div>
    );
  }

  if (!loggedInUser) return null; // redirecting via effect above

  const priceFor = (d) => getMembershipPricing(PLAN_ID, String(d.months), d.price);

  const durationInfo = SILVER_PACKAGE_DURATIONS.find((d) => d.months === durationMonths) || SILVER_PACKAGE_DURATIONS[0];
  const { price: totalPrice, gstPercent } = priceFor(durationInfo);
  const visitsTotal   = durationMonths * SILVER_PACKAGE_SERVICES_PER_MONTH;
  const durationDays  = durationMonths * 30;
  const endDate        = startDate ? addDays(startDate, durationDays) : "";

  const selectedAddress = addresses.find((a) => a.id === selectedAddrId) || null;
  const canProceed = !!startDate && !!slotId && !!selectedAddress && !initiating;

  const maxCarouselIndex = Math.max(0, INCLUDED_SERVICE_CARDS.length - INCLUDED_VISIBLE);
  const carouselStep     = 100 / INCLUDED_VISIBLE;

  // Step 1 -> 2: create the Razorpay order, then hand off to PaymentStep.
  const handleContinueToPayment = async () => {
    if (!canProceed) return;
    setInitiating(true);
    setError("");
    try {
      const slot = timeSlots.find((s) => s.id === slotId);

      const createRes = await fetch(`${BASE_URL}/api/payment/create-order/`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ amount: totalPrice, mobile: userMobile }),
      });
      const createData = await createRes.json();
      if (!createRes.ok || !createData?.success) {
        throw new Error(createData?.message || "Could not initiate payment.");
      }

      setBookingDetails({
        mobile:            userMobile,
        plan_id:            PLAN_ID,
        plan_name:          plan.name,
        visits_total:       visitsTotal,
        duration_days:      durationDays,
        duration_months:    durationMonths,
        free_gift:          durationInfo.freeGift || "",
        price:              totalPrice,
        preferred_slot:     slot?.label || "",
        slot_id:            slotId,
        start_date:         startDate,
        delivery_address:   selectedAddress,
        customer_name:      loggedInUser?.name || loggedInUser?.full_name || "",
        customer_email:     loggedInUser?.email || "",
      });
      setPaymentOrder(createData);
      setStep("payment");
    } catch (e) {
      setError(e.message || "Could not initiate payment. Please try again.");
    } finally {
      setInitiating(false);
    }
  };

  if (success) {
    return (
      <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={S.successCard}>
          <div style={{ fontSize: 56 }}>🎉</div>
          <h2 style={S.successTitle}>Membership Activated!</h2>
          <p style={S.successText}>
            {plan.name} is now active — {visitsTotal} visits available until{" "}
            <strong>{success.expiry_date}</strong>.
          </p>
          <button style={S.primaryBtn} onClick={() => navigate("/profile-dashboard", { state: { openMembership: true } })}>
            Go to My Membership
          </button>
        </div>
      </div>
    );
  }

  if (step === "payment" && paymentOrder && bookingDetails) {
    return (
      <PaymentStep
        plan={plan}
        paymentOrder={paymentOrder}
        bookingDetails={bookingDetails}
        onBack={() => setStep("details")}
        onActivated={(data) => setSuccess(data)}
        onSlotConflict={(msg) => { setError(msg); setSlotId(""); setStep("details"); }}
      />
    );
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <Link to="/subscription-plans" style={S.backLink}><ArrowLeft size={14} /> Back to Plans</Link>

        <div style={S.layout}>
          {/* Left: duration + date + slot + included + address */}
          <div style={S.left}>
            <h1 style={S.title}>Set Up Your {plan.name}</h1>
            <p style={S.subtitle}>Choose your plan duration, start date and preferred recurring visit slot.</p>

            {/* Duration */}
            <div style={S.card}>
              <div style={S.cardLabel}><CalendarDays size={16} color="#4f46e5" /> Plan Duration</div>
              <div style={S.durationGrid}>
                {SILVER_PACKAGE_DURATIONS.map((d) => {
                  const active = durationMonths === d.months;
                  const { price: tierPrice, gstPercent: tierGst } = priceFor(d);
                  return (
                    <button
                      key={d.months}
                      type="button"
                      style={{ ...S.durationCard, ...(active ? S.durationCardActive : {}) }}
                      onClick={() => setDurationMonths(d.months)}
                    >
                      <span style={S.durationLabel}>{d.label}</span>
                      <span style={S.durationPrice}>{formatInr(tierPrice)}</span>
                      <span style={S.durationGstNote}>incl. {tierGst}% GST</span>
                      <span style={S.durationSub}>{d.months * SILVER_PACKAGE_SERVICES_PER_MONTH} visits · {SILVER_PACKAGE_SERVICES_PER_MONTH}/month</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Home Cleaning Kit — updates to the currently selected duration's kit */}
            <div style={S.card}>
              <div style={S.cardLabel}><Gift size={16} color="#4f46e5" /> Home Cleaning Kit</div>
              <div style={S.kitBox}>
                <span style={S.kitPlanTag}>{durationInfo.label}</span>
                <span style={S.kitText}>{durationInfo.freeGift}</span>
              </div>
            </div>

            {/* Start date */}
            <div style={S.card}>
              <div style={S.cardLabel}><CalendarDays size={16} color="#4f46e5" /> Start Date</div>
              <input
                type="date"
                style={S.dateInput}
                value={startDate}
                min={earliestStartDateStr()}
                onChange={(e) => setStartDate(e.target.value)}
              />
              {endDate && (
                <div style={S.endDateHint}>
                  Plan runs for {durationMonths} months — ends on <strong>{endDate}</strong>
                </div>
              )}
            </div>

            {/* Visit slot */}
            <div style={S.card}>
              <div style={S.cardLabel}><Clock3 size={16} color="#4f46e5" /> Preferred Visit Slot (2 hrs 15 min)</div>
              <p style={S.slotHint}>
                {loadingSlots ? "Checking seat availability…" : "Seats are shared — once a slot is full, it can't be booked by other members."}
              </p>
              <div style={S.slotGrid}>
                {timeSlots.map((slot) => {
                  const avail     = slotAvailability.find((s) => s.slot_id === slot.id);
                  const full      = avail ? !avail.availability : false;
                  const seatsLeft = avail ? Math.max(0, avail.seats - avail.booked) : null;
                  return (
                    <button
                      key={slot.id}
                      type="button"
                      disabled={full || loadingSlots}
                      style={{
                        ...S.slotBtn,
                        ...(slotId === slot.id ? S.slotBtnActive : {}),
                        ...(full ? S.slotBtnFull : {}),
                      }}
                      onClick={() => !full && setSlotId(slot.id)}
                    >
                      <span>{slot.label}</span>
                      <span style={S.slotSeatsNote}>
                        {full ? "Fully booked" : seatsLeft != null ? `${seatsLeft} seat${seatsLeft === 1 ? "" : "s"} left` : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Included services — image-card carousel, 4 visible, one-at-a-time nav */}
            <div style={S.card}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "14px" }}>
                <div style={{ ...S.cardLabel, marginBottom: 0 }}><ListChecks size={16} color="#4f46e5" /> What's Included</div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <button
                    type="button"
                    aria-label="Previous services"
                    style={{ ...S.carouselNavBtn, ...(carouselIndex === 0 ? S.carouselNavBtnDisabled : {}) }}
                    disabled={carouselIndex === 0}
                    onClick={() => setCarouselIndex((i) => Math.max(0, i - 1))}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <button
                    type="button"
                    aria-label="Next services"
                    style={{ ...S.carouselNavBtn, ...(carouselIndex >= maxCarouselIndex ? S.carouselNavBtnDisabled : {}) }}
                    disabled={carouselIndex >= maxCarouselIndex}
                    onClick={() => setCarouselIndex((i) => Math.min(maxCarouselIndex, i + 1))}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
              <p style={S.includedNote}>
                17 services, rotated across your visits — {visitsTotal} visits across the full plan (2 hrs 15 min each).
              </p>
              <div style={S.carouselViewport}>
                <div
                  style={{
                    ...S.carouselTrack,
                    width: `${(INCLUDED_SERVICE_CARDS.length / INCLUDED_VISIBLE) * 100}%`,
                    transform: `translateX(-${carouselIndex * carouselStep}%)`,
                  }}
                >
                  {INCLUDED_SERVICE_CARDS.map((s) => (
                    <div key={s.name} style={{ ...S.carouselCard, width: `${100 / INCLUDED_SERVICE_CARDS.length}%` }}>
                      <img src={s.image} alt={s.name} style={S.carouselImg} />
                      <div style={S.carouselCardBody}>
                        <div style={S.carouselCardTitle}>{s.name}</div>
                        <div style={S.carouselCardDesc}>{s.desc}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Address */}
            <div style={S.card}>
              <div style={S.cardLabel}><MapPin size={16} color="#4f46e5" /> Service Address</div>
              {addrLoading ? (
                <div style={S.addrState}>Loading your saved addresses…</div>
              ) : addresses.length === 0 ? (
                <div style={S.addrState}>
                  No saved addresses yet.{" "}
                  <Link to="/profile-dashboard" style={S.inlineLink}>Add one in your profile →</Link>
                </div>
              ) : (
                <div style={S.addrList}>
                  {addresses.map((addr) => {
                    const active = selectedAddrId === addr.id;
                    const full = [addr.line1, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(", ");
                    return (
                      <div
                        key={addr.id}
                        style={{ ...S.addrItem, ...(active ? S.addrItemActive : {}) }}
                        onClick={() => setSelectedAddrId(addr.id)}
                      >
                        <input type="radio" readOnly checked={active} style={{ accentColor: "#4f46e5" }} />
                        <div>
                          <div style={S.addrLabel}>{addr.label || "Address"}</div>
                          <div style={S.addrText}>{full}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right: plan summary + continue */}
          <div style={S.right}>
            <div style={S.summaryCard}>
              <h3 style={S.summaryPlanName}>{plan.name}</h3>
              <p style={S.summaryTagline}>{durationInfo.label} plan</p>

              <div style={S.priceRow}>
                <span style={S.price}>{formatInr(totalPrice)}</span>
                <span style={S.period}>/{durationMonths} months</span>
              </div>
              <div style={S.gstNote}>(incl. {gstPercent}% GST)</div>

              {durationInfo.freeGift && (
                <div style={S.giftBanner}><Gift size={13} /> {durationInfo.freeGift}</div>
              )}

              <div style={S.summaryDivider} />

              <div style={S.summaryRow}><span>Visits Included</span><span style={S.summaryVal}>{visitsTotal} ({SILVER_PACKAGE_SERVICES_PER_MONTH}/month)</span></div>
              <div style={S.summaryRow}><span>Partners</span><span style={S.summaryVal}>2</span></div>
              <div style={S.summaryRow}><span>Valid For</span><span style={S.summaryVal}>{durationMonths} months</span></div>
              <div style={S.summaryRow}>
                <span>Visit Slot</span>
                <span style={S.summaryVal}>{timeSlots.find((s) => s.id === slotId)?.label || "—"}</span>
              </div>
              <div style={S.summaryRow}><span>Start Date</span><span style={S.summaryVal}>{startDate || "—"}</span></div>
              <div style={S.summaryRow}><span>End Date</span><span style={S.summaryVal}>{endDate || "—"}</span></div>

              <div style={S.summaryDivider} />
              <div style={{ ...S.summaryRow, ...S.summaryTotal }}><span>Total Payable</span><span>{formatInr(totalPrice)}</span></div>

              {error && <div style={S.errorBox}>{error}</div>}

              <button
                style={{ ...S.primaryBtn, opacity: canProceed ? 1 : 0.55, cursor: canProceed ? "pointer" : "not-allowed" }}
                disabled={!canProceed}
                onClick={handleContinueToPayment}
              >
                {initiating ? "Preparing Payment…" : <>Continue to Payment — {formatInr(totalPrice)} <ArrowRight size={15} /></>}
              </button>
              <div style={S.secureNote}><ShieldCheck size={13} /> Secured by Razorpay (test mode)</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const S = {
  page:      { background: "#f8fafc", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1100px", margin: "0 auto" },
  backLink:  { display: "inline-flex", alignItems: "center", gap: "6px", marginBottom: "18px", color: "#4f46e5", fontWeight: 700, fontSize: "14px", textDecoration: "none", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit" },

  layout: { display: "grid", gridTemplateColumns: "1fr 360px", gap: "24px", alignItems: "start" },
  left:   { display: "flex", flexDirection: "column", gap: "16px", minWidth: 0 },
  right:  { minWidth: 0 },

  title:    { margin: 0, fontSize: "28px", fontWeight: 800 },
  subtitle: { margin: "8px 0 4px", color: "#6b7280", fontSize: "14px" },

  card:      { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "18px 20px" },
  cardLabel: { display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 800, color: "#111827", marginBottom: "14px" },

  durationGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px" },
  durationCard: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px", border: "1.5px solid #e5e7eb", background: "#fff", borderRadius: "14px", padding: "14px 14px", cursor: "pointer", textAlign: "left" },
  durationCardActive: { border: "1.5px solid #4f46e5", background: "#eef2ff" },
  durationLabel: { fontSize: "13px", fontWeight: 700, color: "#6b7280" },
  durationPrice: { fontSize: "19px", fontWeight: 800, color: "#111827" },
  durationSub:   { fontSize: "11px", color: "#9ca3af", fontWeight: 600 },
  durationGstNote: { fontSize: "10.5px", color: "#9ca3af", fontWeight: 600 },
  kitBox:     { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", background: "#faf5ff", border: "1px solid #e9d5ff", borderRadius: "14px", padding: "14px 16px" },
  kitPlanTag: { background: "#a855f7", color: "#fff", fontSize: "11px", fontWeight: 800, borderRadius: "999px", padding: "4px 12px", whiteSpace: "nowrap" },
  kitText:    { fontSize: "13.5px", fontWeight: 700, color: "#581c87", lineHeight: 1.5 },

  dateInput: { width: "100%", maxWidth: "260px", height: "44px", padding: "0 14px", borderRadius: "12px", border: "1px solid #d6deeb", fontSize: "14px", outline: "none", boxSizing: "border-box" },
  endDateHint: { marginTop: "10px", fontSize: "12px", color: "#6b7280" },

  slotHint: { margin: "-6px 0 12px", fontSize: "12px", color: "#6b7280" },
  slotGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px" },
  slotBtn:  { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px", border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", borderRadius: "12px", padding: "12px 10px", fontSize: "13px", fontWeight: 700, cursor: "pointer" },
  slotBtnActive: { border: "1.5px solid #4f46e5", background: "#eef2ff", color: "#4338ca" },
  slotBtnFull: { background: "#f9fafb", color: "#9ca3af", borderColor: "#e5e7eb", cursor: "not-allowed", opacity: 0.75 },
  slotSeatsNote: { fontSize: "11px", fontWeight: 600, color: "#9ca3af" },

  includedNote: { margin: "0 0 14px", fontSize: "12px", color: "#6b7280", lineHeight: 1.6 },

  carouselNavBtn: { width: "30px", height: "30px", borderRadius: "50%", border: "1px solid #e5e7eb", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#4f46e5" },
  carouselNavBtnDisabled: { opacity: 0.35, cursor: "not-allowed" },
  carouselViewport: { overflow: "hidden", borderRadius: "14px" },
  carouselTrack: { display: "flex", transition: "transform 0.3s ease" },
  carouselCard: { flexShrink: 0, padding: "0 6px", boxSizing: "border-box" },
  carouselImg:  { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", borderRadius: "12px", display: "block" },
  carouselCardBody: { padding: "8px 2px 0" },
  carouselCardTitle: { fontSize: "12.5px", fontWeight: 700, color: "#111827" },
  carouselCardDesc:  { fontSize: "11px", color: "#9ca3af", marginTop: "2px", lineHeight: 1.4 },

  addrState: { fontSize: "13px", color: "#6b7280" },
  inlineLink: { color: "#4f46e5", fontWeight: 700, textDecoration: "none" },
  addrList: { display: "flex", flexDirection: "column", gap: "10px" },
  addrItem: { display: "flex", alignItems: "flex-start", gap: "10px", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "12px", cursor: "pointer" },
  addrItemActive: { border: "1.5px solid #4f46e5", background: "#f8faff" },
  addrLabel: { fontSize: "13px", fontWeight: 800, color: "#111827" },
  addrText:  { fontSize: "12px", color: "#6b7280", marginTop: "3px", lineHeight: 1.5 },

  summaryCard: { position: "sticky", top: "20px", background: "#fff", border: "1px solid #e5e7eb", borderRadius: "20px", padding: "24px 22px" },
  summaryPlanName: { margin: 0, fontSize: "20px", fontWeight: 800 },
  summaryTagline:  { margin: "4px 0 16px", fontSize: "12px", color: "#9ca3af", fontWeight: 600 },

  priceRow: { display: "flex", alignItems: "baseline", gap: "6px", marginBottom: "10px" },
  price:    { fontSize: "32px", fontWeight: 800, letterSpacing: "-0.5px" },
  period:   { fontSize: "13px", color: "#9ca3af", fontWeight: 600 },
  gstNote:  { fontSize: "11px", color: "#9ca3af", fontWeight: 600, marginBottom: "12px" },

  giftBanner: { display: "flex", alignItems: "center", gap: "6px", background: "#faf5ff", border: "1px solid #e9d5ff", color: "#7e22ce", fontSize: "12px", fontWeight: 700, borderRadius: "10px", padding: "8px 12px", marginBottom: "16px" },

  summaryDivider: { height: "1px", background: "#eee", margin: "16px 0" },
  summaryRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#6b7280", marginBottom: "10px" },
  summaryVal: { color: "#111827", fontWeight: 700 },
  summaryTotal: { fontSize: "16px", fontWeight: 800, color: "#111827", marginBottom: "18px" },

  errorBox: { background: "#fef2f2", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 14px", color: "#b91c1c", fontSize: "13px", marginBottom: "14px" },

  primaryBtn: { width: "100%", border: "none", borderRadius: "14px", padding: "15px 0", fontSize: "15px", fontWeight: 800, background: "#4f46e5", color: "#fff", marginBottom: "10px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  secureNote: { display: "flex", alignItems: "center", justifyContent: "center", gap: "6px", fontSize: "11px", color: "#9ca3af", fontWeight: 600 },

  testBanner: { background: "#fefce8", border: "1px solid #fde68a", borderRadius: "12px", padding: "12px 16px", fontSize: "14px", color: "#92400e", marginBottom: "18px" },

  successCard:  { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "22px", padding: "40px 34px", textAlign: "center", maxWidth: "420px" },
  successTitle: { margin: "12px 0 8px", fontSize: "24px", fontWeight: 800 },
  successText:  { margin: "0 0 22px", color: "#6b7280", fontSize: "14px", lineHeight: 1.7 },
};

export default SilverPackageCheckout;
